/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Tab, Tabs } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import API from '../../../../api';
import { ContentContext, GoogleMapContext, SnackbarContext } from '../../../../contexts';
import Handler from '../../../../Handler';
import { useLabel } from '../../../../hooks';
import { scrollToFirstError } from '../../../../utils/forms';
import { useReduction } from '../../hooks';
import { reductionFormValidations, reductionInitialValues } from '../form';
import { BizDetail } from './BizDetail';
import { MonitoringSystem } from './MonitoringSystem';
import { OtherEvidence } from './OtherEvidence';

export const ReductionForm = (props) => {
  const { reduction, setActionOpen, setSelectedAction, setSelectedNextAction } = props;
  const { slug } = useParams();
  const history = useHistory();
  const { postReduction, putReduction, getReduction } = useReduction();
  const { t, i18n } = useTranslation();
  const { ERROR } = useLabel();
  const { methodologyInfo, monitoringInfo } = useContext(ContentContext);
  const { setAddress } = useContext(GoogleMapContext);
  const [updatedEvidence, setUpdatedEvidence] = useState([]);

  const { updateSnackbar } = useContext(SnackbarContext);
  const [step, setStep] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const [originData, setOriginData] = useState(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: reductionInitialValues,
  });

  const formData = watch();
  const validations = reductionFormValidations(i18n.language === 'en');

  console.log('formData', formData);
  /* 이전으로 이동 */
  const handlePrev = () => {
    if (tabIndex > 0) {
      setTabIndex(tabIndex - 1);
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  /* 다음으로 이동 */
  const goToNext = () => {
    getReduction();
    setTabIndex(tabIndex + 1);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  /* Form Request 요청 */
  const onSubmit = useCallback(
    async (data, shouldGoNext = true) => {
      if (tabIndex === 0) {
        setTabIndex(1);
        return;
      }
      // Step별 검증
      if (tabIndex === 1) {
        if (!data.startDate || !data.endDate) {
          updateSnackbar({
            type: 'error',
            open: true,
            message: validations.startDate.required || '시작일과 종료일을 선택해주세요.',
          });
          return;
        }

        if (!data.realReductionBase || !data.realReductionBusiness || !data.realReductionLeak) {
          updateSnackbar({
            type: 'error',
            open: true,
            message: `${ERROR['REQUIRED_INFO']}\n- ${t('START_DATE')}, ${t('END_DATE')}, ${t('BASELINE_EMISSION')}, ${t(
              'BIZ_EMISSION',
            )}, ${t('LEAKAGE')}`,
          });
          return;
        }

        let new_startDate = new Date(data.startDate).getTime();
        let new_endDate = new Date(data.endDate).getTime();
        if (new_endDate < new_startDate) {
          updateSnackbar({
            type: 'error',
            open: true,
            message: t('PLEASE_VERIFY_MONITORING'),
          });
          return;
        }
      }

      if (tabIndex === 2) {
        // 필수 증빙서류 검증은 나중에 추가
      }

      if (slug) {
        const result = await putReduction(data);
        if (!result) return;

        if (tabIndex === 2 && shouldGoNext) {
          if (originData?.type === 'Improve') {
            setSelectedAction({
              seq: slug,
            });
            setSelectedNextAction('ImproveReductionCertComplete');
            setActionOpen(true);
          } else {
            API.ReductionCert.PostNextAction(slug, {
              nextAction: 'ReductionCertSubmit',
            })
              .then((res) => {
                history.push('/request_complete?type=reduction');
              })
              .catch((err) => {
                updateSnackbar({
                  type: 'error',
                  open: true,
                  message: err.data?.message || '오류가 발생했습니다.',
                });
              });
          }
        } else {
          if (shouldGoNext && tabIndex < 2) {
            goToNext();
          }
        }
      } else {
        await postReduction(data);
        if (shouldGoNext && tabIndex < 2) {
          goToNext();
        }
      }
    },
    [
      step,
      ERROR,
      t,
      updateSnackbar,
      tabIndex,
      slug,
      putReduction,
      originData,
      setSelectedAction,
      setSelectedNextAction,
      setActionOpen,
      history,
      goToNext,
      postReduction,
      validations,
    ],
  );

  const onError = (errors, event) => {
    if (event) {
      event.preventDefault();
    }
    scrollToFirstError(errors);
  };

  const nextLabel = useMemo(() => {
    if (tabIndex === 2) {
      return originData?.type === 'Improve' ? t('COMPLEMENT_APPLY') : t('REDUCTION_SUBMIT');
    }
    return tabIndex !== 0 ? t('TEMP_SAVE_NEXT') : t('NEXT');
  }, [tabIndex, originData?.type, t]);

  useEffect(() => {
    if (reduction && monitoringInfo) {
      setOriginData(reduction);
    }
  }, [reduction, monitoringInfo]);

  useEffect(() => {
    if (slug) {
      getReduction();
    }
  }, [slug]);

  useEffect(() => {
    if (originData && monitoringInfo) {
      const lastDoc = originData.reductionCert.project.lastDoc;

      /* 초기 로드 시, 주소 및 증빙자료 세팅 */
      setAddress(Handler.jsonParser(lastDoc.location)?.addr1?.formatted_address);
      setUpdatedEvidence(
        originData.evidenceMapper.map((el) => ({ seq: el.seq, data: el.file, evidenceSeq: el.evidence?.seq })),
      );

      console.log('evidenceList useEffect', originData.evidenceMapper);
      console.log(
        'evidenceList useEffect',
        originData.evidenceMapper.map((el) => ({ seq: el.seq, data: el.file, evidenceSeq: el.evidence?.seq })),
      );
      reset({
        ...reductionInitialValues,
        isExported: lastDoc.isExported,
        title: `${
          i18n.language === 'ko' ? originData.reductionCert.project.title : originData.reductionCert.project.titleEn
        }`,
        projectId: originData.reductionCert.project.id,
        location: lastDoc.location,
        workplaceImages: lastDoc.workplaceImages,
        area: originData.reductionCert.project.methodology.area,
        subArea: originData.reductionCert.project.methodology.subArea,
        methodologySeq: `${
          i18n.language === 'ko'
            ? originData.reductionCert.project.methodology.title
            : originData.reductionCert.project.methodology.titleEn
        }`,
        startDate: originData.startDate ? dayjs(Handler.dbCommaDateToDashDate(originData.startDate)) : dayjs(),
        endDate: originData.endDate ? dayjs(Handler.dbCommaDateToDashDate(originData.endDate)) : dayjs().add(1, 'year'),
        monitorDegree: originData.reductionCert.monitorDegree,
        reductionBase: originData.reductionCert.reductionBase,
        reductionBusiness: originData.reductionCert.reductionBusiness,
        reductionLeak: originData.reductionCert.reductionLeak,
        reduction: originData.reductionCert.reduction,
        realReductionBase: originData.realReductionBase,
        realReductionBusiness: originData.realReductionBusiness,
        realReductionLeak: originData.realReductionLeak,
        realReduction: originData.realReduction,
        beforeImages: originData.beforeImages ? originData.beforeImages : [],
        afterImages: originData.afterImages ? originData.afterImages : [],
        evidence:
          originData.evidenceMapper?.length > 0
            ? originData.evidenceMapper.map((el) => ({ seq: el.seq, data: el.file, evidenceSeq: el.evidence?.seq }))
            : [],
        additionalEvidence: originData.additionalEvidence ? originData.additionalEvidence : [],
      });
    }
  }, [originData, monitoringInfo, i18n.language, reset]);

  useEffect(() => {
    if (formData.realReductionBase && formData.realReductionBusiness && formData.realReductionLeak) {
      if (
        Number.isFinite(Number(formData.realReductionBase)) &&
        Number.isFinite(Number(formData.realReductionBusiness)) &&
        Number.isFinite(Number(formData.realReductionLeak))
      ) {
        setValue(
          'realReduction',
          Number(formData.realReductionBase) -
            Number(formData.realReductionBusiness) -
            Number(formData.realReductionLeak),
          { shouldValidate: false },
        );
      } else {
        setValue('realReduction', '', { shouldValidate: false });
      }
    } else {
      setValue('realReduction', '', { shouldValidate: false });
    }
  }, [formData.realReductionBase, formData.realReductionBusiness, formData.realReductionLeak, setValue]);

  /* 기타 증빙자료 목록 */
  const evidenceList = useMemo(() => {
    if (!reduction?.evidenceMapper) return [];
    return [...new Map(reduction?.evidenceMapper?.map((item) => [item.seq, item])).values()];
  }, [reduction]);

  useEffect(() => {
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
  }, [tabIndex]);

  return (
    <div>
      <form
        id='reduction-form'
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(onSubmit, onError)(e);
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(event, newValue) => setTabIndex(newValue)}
          sx={{
            'width': '100%',
            '& .MuiTabs-flexContainer': {
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              borderBottom: '1px solid #e0e0e0',
            },
          }}
        >
          <Tab label={t('CHECK_BUSINESS_SUMMARY')} value={0} onClick={(e) => e.preventDefault()} />
          <Tab
            label={t('REDUCTION_MONITORING_SYSTEM')}
            disabled={!slug}
            value={1}
            onClick={(e) => e.preventDefault()}
          />
          <Tab label={t('OTHER_DOCUMENTS')} disabled={!slug} value={2} onClick={(e) => e.preventDefault()} />
        </Tabs>
        <div
          role='tabpanel'
          id={`simple-tabpanel-${step}`}
          aria-labelledby={`simple-tab-${step}`}
          className='w-[945px]'
        >
          {tabIndex === 0 && (
            <Box
              sx={{
                pt: 4,
              }}
            >
              {/* Step 0: 사업 개요 - 나중에 개별 컴포넌트로 분리 */}
              <BizDetail reduction={reduction} />
            </Box>
          )}
          {tabIndex === 1 && (
            <Box
              sx={{
                pt: 4,
              }}
            >
              {/* Step 1: 감축 모니터링 시스템 - 나중에 개별 컴포넌트로 분리 */}
              <MonitoringSystem
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                control={control}
              />
            </Box>
          )}
          {tabIndex === 2 && (
            <Box
              sx={{
                pt: 4,
              }}
            >
              {/* Step 2: 기타 서류 - 나중에 개별 컴포넌트로 분리 */}
              <OtherEvidence
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                control={control}
                evidenceList={evidenceList}
                updatedEvidence={updatedEvidence}
              />
            </Box>
          )}
        </div>
        <article className='flex justify-between w-full gap-8'>
          <Button variant='outlined' onClick={handlePrev}>
            {t('PREV')}
          </Button>
          <div className='flex items-center gap-2'>
            {tabIndex > 0 && (
              <Button
                variant='outlined'
                color='primary'
                type='button'
                form='reduction-form'
                onClick={() => {
                  handleSubmit((data) => onSubmit(data, false), onError)();
                }}
              >
                {t('TEMP_SAVE')}
              </Button>
            )}
            <Button
              variant='contained'
              color='primary'
              type={'submit'}
              form='reduction-form'
              // onClick={() => {
              //   if (tabIndex !== 0) {
              //     handleSubmit((data) => onSubmit(data, false), onError)();
              //   } else {
              //     setTabIndex(1);
              //   }
              // }}
            >
              {nextLabel}
            </Button>
          </div>
        </article>
      </form>
    </div>
  );
};
